/**
 * Survey Engine - Store-Agnostic
 * Supports 7 question types, multiple trigger modes, cookie frequency control,
 * Klaviyo integration, and CSS-based animations.
 *
 * Question types: single-choice, multi-choice, rating, nps, emoji, text, thumbs
 * Trigger modes: immediate, exit-intent, scroll, time-delay, idle
 */
(function () {
  'use strict';

  // ── Cookie Utilities ──

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + (days * 86400000));
    document.cookie = name + '=' + encodeURIComponent(value) + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }

  function getCookie(name) {
    var match = document.cookie.split(';').find(function (c) {
      return c.trim().indexOf(name + '=') === 0;
    });
    return match ? decodeURIComponent(match.split('=')[1]) : null;
  }

  // ── SVG Icons ──

  var ICONS = {
    close: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    chat: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    thumbUp: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3m7-2V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    thumbDown: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M17 2H20a2 2 0 012 2v7a2 2 0 01-2 2h-3m-7 2v4a3 3 0 003 3l4-9V2H6.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    star: '<svg width="24" height="24" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/></svg>',
    starEmpty: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>',
    check: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    heart: '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>'
  };

  var EMOJIS = ['😞', '😕', '😐', '🙂', '😍'];
  var EMOJI_LABELS = ['Very unhappy', 'Unhappy', 'Neutral', 'Happy', 'Very happy'];

  // ── SurveyEngine ──

  window.SurveyEngine = function SurveyEngine(config) {
    this.config = config || {};
    this.surveyId = config.id || 'survey-' + Date.now();
    this.type = config.type || 'generic';
    this.questions = config.questions || [];
    this.currentIndex = 0;
    this.answers = {};
    this.container = null;
    this.isVisible = false;
    this.klaviyo = null;

    var settings = config.settings || {};
    this.cookieName = settings.cookieName || 'sv_' + this.surveyId;
    this.cookieDays = settings.cookieDays || 7;
    this.maxShows = settings.maxShows || 1;
    this.trigger = settings.trigger || 'immediate';
    this.triggerDelay = settings.triggerDelay || 5000;
    this.triggerScroll = settings.triggerScroll || 50;
    this.triggerIdle = settings.triggerIdle || 60000;
    this.minPageViews = settings.minPageViews || 0;
    this.animation = settings.animation || 'fade-in';
    this.position = settings.position || 'embedded';
    this.thankYou = config.thankYou || { title: 'Thank you!', subtitle: 'Your feedback helps us improve.' };

    // Klaviyo
    var klaviyoKey = settings.klaviyoPublicKey || null;
    if (klaviyoKey && typeof window.KlaviyoIntegration !== 'undefined') {
      this.klaviyo = new window.KlaviyoIntegration(klaviyoKey);
    }

    this.apiUrl = settings.apiUrl || 'https://quiz-n-survey.netlify.app/.netlify/functions/survey-submit';
  };

  var proto = SurveyEngine.prototype;

  // ── Lifecycle ──

  proto.init = function (containerEl) {
    if (!this.canShow()) return;
    this.container = typeof containerEl === 'string' ? document.querySelector(containerEl) : containerEl;
    if (!this.container) return;
    this.attachTrigger();
  };

  proto.meetsPageViewRequirement = function () {
    if (!this.minPageViews) return true;
    if (window.IUHPopupOrchestrator) {
      return window.IUHPopupOrchestrator.pageViews() >= this.minPageViews;
    }
    var pv = parseInt(getCookie('iuh_page_views') || '0', 10);
    return pv >= this.minPageViews;
  };

  proto.canShow = function () {
    if (!this.meetsPageViewRequirement()) return false;
    var cookie = getCookie(this.cookieName);
    if (!cookie) return true;
    try {
      var data = JSON.parse(cookie);
      return data.count < this.maxShows;
    } catch (e) { return true; }
  };

  proto.markShown = function () {
    var cookie = getCookie(this.cookieName);
    var data;
    try { data = cookie ? JSON.parse(cookie) : { count: 0 }; }
    catch (e) { data = { count: 0 }; }
    data.count++;
    data.lastShown = new Date().toISOString();
    setCookie(this.cookieName, JSON.stringify(data), this.cookieDays);
  };

  proto.trackProductView = function () {
    try {
      var count = parseInt(sessionStorage.getItem('sv_product_views') || '0');
      sessionStorage.setItem('sv_product_views', count + 1);
      console.log('SurveyEngine: Product views:', count + 1);
    } catch (e) { }
  };

  proto.getProductViewCount = function () {
    try {
      return parseInt(sessionStorage.getItem('sv_product_views') || '0');
    } catch (e) { return 0; }
  };

  proto.attachTrigger = function () {
    var self = this;
    var start = function () {
      if (!self.canShow()) return;
      self._attachTriggerImpl();
    };
    if (window.IUHPopupOrchestrator) {
      window.IUHPopupOrchestrator.onReady(start);
    } else {
      start();
    }
  };

  proto._attachTriggerImpl = function () {
    var self = this;

    switch (this.trigger) {
      case 'immediate':
        this.show();
        break;

      case 'time-delay':
        setTimeout(function () { self.show(); }, this.triggerDelay);
        break;

      case 'scroll':
        var scrollHandler = function () {
          var scrollPct = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
          if (scrollPct >= self.triggerScroll) {
            window.removeEventListener('scroll', scrollHandler);
            self.show();
          }
        };
        window.addEventListener('scroll', scrollHandler, { passive: true });
        break;

      case 'exit-intent':
        var exitHandler = function (e) {
          if (e.clientY > 10) return;
          if (window.__iuhExitShown) return;
          if (self.show()) {
            document.removeEventListener('mouseleave', exitHandler);
            window.__iuhExitShown = true;
          }
        };
        document.addEventListener('mouseleave', exitHandler);
        // Touch exit surveys use idle timing; never trap browser navigation.
        if (window.matchMedia('(hover: none), (pointer: coarse)').matches) {
          this.trigger = 'idle';
          this._attachTriggerImpl();
        }
        break;

      case 'idle':
        var idleTimer;
        var resetIdle = function () {
          clearTimeout(idleTimer);
          idleTimer = setTimeout(function () { self.show(); }, self.triggerIdle);
        };
        ['mousemove', 'keydown', 'scroll', 'touchstart'].forEach(function (evt) {
          document.addEventListener(evt, resetIdle, { passive: true, once: false });
        });
        resetIdle();
        break;

      default:
        this.show();
    }
  };

  proto.show = function () {
    if (this.isVisible || !this.canShow()) return;
    if (this.trigger !== 'manual' && window.IUHPopupOrchestrator && !window.IUHPopupOrchestrator.canPrompt()) return false;
    // Collision prevention: don't show if another survey is active
    if (window.__surveyActive && this.trigger !== 'manual') return;

    this.isVisible = true;
    window.__surveyActive = true; // Set global mutex
    this.markShown();
    this.renderSurvey();

    if (this.klaviyo) {
      this.klaviyo.track('Survey Shown', { 'Survey ID': this.surveyId, 'Survey Type': this.type });
    }
    return true;
  };

  proto.hide = function (completed) {
    this.isVisible = false;
    window.__surveyActive = false; // Release global mutex
    if (this.container) {
      this.container.classList.add('sv-hiding');
      var self = this;
      setTimeout(function () {
        self.container.innerHTML = '';
        self.container.classList.remove('sv-hiding', 'sv-visible');
      }, 300);
    }

    if (!completed && this.klaviyo) {
      this.klaviyo.track('Survey Dismissed', {
        'Survey ID': this.surveyId,
        'Questions Answered': Object.keys(this.answers).length,
        'Total Questions': this.questions.length
      });
    }
  };

  // ── Rendering ──

  proto.renderSurvey = function () {
    if (this.questions.length === 0) return;
    this.renderQuestion(0);
    this.container.classList.add('sv-visible', 'sv-' + this.animation);
  };

  proto.renderQuestion = function (index) {
    var q = this.questions[index];
    if (!q) { this.showThankYou(); return; }
    this.currentIndex = index;

    var html = '<div class="sv-card sv-card--' + this.position + '">';
    html += '<button class="sv-close" aria-label="Close">' + ICONS.close + '</button>';
    html += '<div class="sv-question-wrap">';
    html += '<h4 class="sv-question">' + (q.question || q.title) + '</h4>';
    if (q.subtitle) html += '<p class="sv-subtitle">' + q.subtitle + '</p>';
    html += this.renderQuestionType(q);
    html += '</div>';

    // Progress
    if (this.questions.length > 1) {
      html += '<div class="sv-progress"><div class="sv-progress-bar" style="width:' +
        (((index + 1) / this.questions.length) * 100) + '%"></div></div>';
    }
    html += '</div>';

    this.container.innerHTML = html;
    this.attachQuestionEvents(q);
  };

  proto.renderQuestionType = function (q) {
    switch (q.type) {
      case 'single-choice': return this.renderSingleChoice(q);
      case 'multi-choice': return this.renderMultiChoice(q);
      case 'rating': return this.renderRating(q);
      case 'nps': return this.renderNPS(q);
      case 'emoji': return this.renderEmoji(q);
      case 'text': return this.renderText(q);
      case 'thumbs': return this.renderThumbs(q);
      default: return this.renderSingleChoice(q);
    }
  };

  proto.renderSingleChoice = function (q) {
    var html = '<div class="sv-options sv-options--single" data-qid="' + q.id + '">';
    (q.options || []).forEach(function (opt) {
      html += '<button class="sv-option" data-value="' + opt.id + '">' +
        '<span class="sv-option-label">' + opt.label + '</span>' +
        '</button>';
    });
    html += '</div>';
    return html;
  };

  proto.renderMultiChoice = function (q) {
    var html = '<div class="sv-options sv-options--multi" data-qid="' + q.id + '">';
    (q.options || []).forEach(function (opt) {
      html += '<button class="sv-option" data-value="' + opt.id + '">' +
        '<span class="sv-option-check">' + ICONS.check + '</span>' +
        '<span class="sv-option-label">' + opt.label + '</span>' +
        '</button>';
    });
    html += '</div>';
    html += '<button class="sv-submit-btn" data-qid="' + q.id + '">Submit</button>';
    return html;
  };

  proto.renderRating = function (q) {
    var scale = q.scale || 5;
    var html = '<div class="sv-rating" data-qid="' + q.id + '">';
    html += '<div class="sv-stars">';
    for (var i = 1; i <= scale; i++) {
      html += '<button class="sv-star" data-value="' + i + '" title="' + i + ' star' + (i > 1 ? 's' : '') + '">' + ICONS.starEmpty + '</button>';
    }
    html += '</div>';
    if (q.labels) {
      html += '<div class="sv-rating-labels"><span>' + (q.labels.low || '') + '</span><span>' + (q.labels.high || '') + '</span></div>';
    }
    html += '</div>';
    return html;
  };

  proto.renderNPS = function (q) {
    var html = '<div class="sv-nps" data-qid="' + q.id + '">';
    html += '<div class="sv-nps-scale">';
    for (var i = 0; i <= 10; i++) {
      var cls = 'sv-nps-btn';
      if (i <= 6) cls += ' sv-nps--detractor';
      else if (i <= 8) cls += ' sv-nps--passive';
      else cls += ' sv-nps--promoter';
      html += '<button class="' + cls + '" data-value="' + i + '">' + i + '</button>';
    }
    html += '</div>';
    html += '<div class="sv-nps-labels"><span>Not at all likely</span><span>Extremely likely</span></div>';
    html += '</div>';
    return html;
  };

  proto.renderEmoji = function (q) {
    var html = '<div class="sv-emoji" data-qid="' + q.id + '">';
    EMOJIS.forEach(function (emoji, i) {
      html += '<button class="sv-emoji-btn" data-value="' + (i + 1) + '" title="' + EMOJI_LABELS[i] + '">' +
        '<span class="sv-emoji-icon">' + emoji + '</span>' +
        '<span class="sv-emoji-label">' + EMOJI_LABELS[i] + '</span>' +
        '</button>';
    });
    html += '</div>';
    return html;
  };

  proto.renderText = function (q) {
    var html = '<div class="sv-text" data-qid="' + q.id + '">';
    html += '<textarea class="sv-textarea" placeholder="' + (q.placeholder || 'Share your thoughts...') + '" rows="3"></textarea>';
    html += '<button class="sv-submit-btn" data-qid="' + q.id + '">Submit</button>';
    html += '</div>';
    return html;
  };

  proto.renderThumbs = function (q) {
    var html = '<div class="sv-thumbs" data-qid="' + q.id + '">';
    html += '<button class="sv-thumb sv-thumb--up" data-value="positive" title="Yes">' + ICONS.thumbUp + '</button>';
    html += '<button class="sv-thumb sv-thumb--down" data-value="negative" title="No">' + ICONS.thumbDown + '</button>';
    html += '</div>';
    return html;
  };

  // ── Events ──

  proto.attachQuestionEvents = function (q) {
    var self = this;
    var card = this.container.querySelector('.sv-card');

    // Close button
    var closeBtn = this.container.querySelector('.sv-close');
    if (closeBtn) closeBtn.addEventListener('click', function () { self.hide(false); });

    switch (q.type) {
      case 'single-choice':
        this.container.querySelectorAll('.sv-option').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var value = this.getAttribute('data-value');
            var option = (q.options || []).find(function (o) { return o.id === value; });
            self.handleAnswer(q.id, value, option);
          });
        });
        break;

      case 'multi-choice':
        this.container.querySelectorAll('.sv-option').forEach(function (btn) {
          btn.addEventListener('click', function () {
            this.classList.toggle('selected');
          });
        });
        var submitBtn = this.container.querySelector('.sv-submit-btn');
        if (submitBtn) {
          submitBtn.addEventListener('click', function () {
            var selected = [];
            self.container.querySelectorAll('.sv-option.selected').forEach(function (el) {
              selected.push(el.getAttribute('data-value'));
            });
            if (selected.length > 0) self.handleAnswer(q.id, selected);
          });
        }
        break;

      case 'rating':
        this.container.querySelectorAll('.sv-star').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var value = parseInt(this.getAttribute('data-value'));
            // Highlight stars
            self.container.querySelectorAll('.sv-star').forEach(function (s, idx) {
              s.innerHTML = idx < value ? ICONS.star : ICONS.starEmpty;
              s.classList.toggle('active', idx < value);
            });
            setTimeout(function () { self.handleAnswer(q.id, value); }, 400);
          });
          // Hover effect
          btn.addEventListener('mouseenter', function () {
            var val = parseInt(this.getAttribute('data-value'));
            self.container.querySelectorAll('.sv-star').forEach(function (s, idx) {
              s.innerHTML = idx < val ? ICONS.star : ICONS.starEmpty;
            });
          });
        });
        var ratingWrap = this.container.querySelector('.sv-rating');
        if (ratingWrap) {
          ratingWrap.addEventListener('mouseleave', function () {
            self.container.querySelectorAll('.sv-star').forEach(function (s) {
              if (!s.classList.contains('active')) s.innerHTML = ICONS.starEmpty;
            });
          });
        }
        break;

      case 'nps':
        this.container.querySelectorAll('.sv-nps-btn').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var value = parseInt(this.getAttribute('data-value'));
            self.container.querySelectorAll('.sv-nps-btn').forEach(function (b) { b.classList.remove('selected'); });
            this.classList.add('selected');
            setTimeout(function () { self.handleAnswer(q.id, value); }, 400);
          });
        });
        break;

      case 'emoji':
        this.container.querySelectorAll('.sv-emoji-btn').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var value = parseInt(this.getAttribute('data-value'));
            self.container.querySelectorAll('.sv-emoji-btn').forEach(function (b) { b.classList.remove('selected'); });
            this.classList.add('selected');
            setTimeout(function () { self.handleAnswer(q.id, value); }, 400);
          });
        });
        break;

      case 'text':
        var textSubmit = this.container.querySelector('.sv-submit-btn');
        if (textSubmit) {
          textSubmit.addEventListener('click', function () {
            var textarea = self.container.querySelector('.sv-textarea');
            var value = textarea ? textarea.value.trim() : '';
            if (value) self.handleAnswer(q.id, value);
          });
        }
        break;

      case 'thumbs':
        this.container.querySelectorAll('.sv-thumb').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var value = this.getAttribute('data-value');
            this.classList.add('selected');
            setTimeout(function () { self.handleAnswer(q.id, value); }, 300);
          });
        });
        break;
    }
  };

  proto.handleAnswer = function (questionId, value, option) {
    this.answers[questionId] = {
      value: value,
      klaviyoProperty: option && option.klaviyoProperty || null,
      klaviyoValue: option && option.klaviyoValue || value
    };

    // Check for follow-up question
    var q = this.questions[this.currentIndex];
    if (q && q.followUp && option && option.showFollowUp) {
      this.renderFollowUp(q.followUp, questionId);
      return;
    }

    // Track step
    if (this.klaviyo) {
      this.klaviyo.track('Survey Step Completed', {
        'Survey ID': this.surveyId,
        'Question': questionId,
        'Answer': typeof value === 'object' ? JSON.stringify(value) : String(value)
      });
    }

    // Advance
    if (this.currentIndex + 1 < this.questions.length) {
      this.renderQuestion(this.currentIndex + 1);
    } else {
      this.completeSurvey();
    }
  };

  proto.renderFollowUp = function (followUp, parentId) {
    var wrap = this.container.querySelector('.sv-question-wrap');
    if (!wrap) return;
    var self = this;
    var html = '<div class="sv-followup">';
    html += '<textarea class="sv-textarea" placeholder="' + (followUp.placeholder || 'Tell us more...') + '" rows="2"></textarea>';
    html += '<button class="sv-submit-btn">Submit</button>';
    html += '</div>';

    var div = document.createElement('div');
    div.innerHTML = html;
    wrap.appendChild(div.firstElementChild);

    wrap.querySelector('.sv-followup .sv-submit-btn').addEventListener('click', function () {
      var val = wrap.querySelector('.sv-followup .sv-textarea').value.trim();
      if (val) self.answers[parentId + '_followup'] = { value: val };
      if (self.currentIndex + 1 < self.questions.length) {
        self.renderQuestion(self.currentIndex + 1);
      } else {
        self.completeSurvey();
      }
    });
  };

  var API_ENDPOINT = 'https://quiz-n-survey.netlify.app/.netlify/functions/survey-submit';

  proto.completeSurvey = function () {
    var self = this;

    // Send to Netlify
    var payload = {
      survey_type: this.type,
      store_domain: window.Shopify ? window.Shopify.shop : window.location.hostname,
      responses: this.answers,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      user_agent: navigator.userAgent
    };

    // Add optional context
    if (window.__st && window.__st.cid) payload.customer_id = window.__st.cid;
    if (window.meta && window.meta.product) {
      payload.product_id = window.meta.product.id;
      payload.product_title = window.meta.product.variants[0].name;
    }

    if (navigator.sendBeacon) {
      var blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(this.apiUrl, blob);
    } else {
      fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(function (e) { console.error('Survey submit error:', e); });
    }

    if (this.klaviyo) {
      this.klaviyo.trackSurvey(this.surveyId, this.type, this.answers);

      // NPS-specific tracking
      var npsAnswer = null;
      this.questions.forEach(function (q) {
        if (q.type === 'nps' && this.answers[q.id]) {
          npsAnswer = this.answers[q.id].value;
        }
      }.bind(this));

      if (npsAnswer !== null) {
        var category = npsAnswer <= 6 ? 'detractor' : (npsAnswer <= 8 ? 'passive' : 'promoter');
        this.klaviyo.track('NPS Score Submitted', {
          'Score': npsAnswer,
          'Category': category,
          'Survey ID': this.surveyId
        });
      }
    }
    this.showThankYou();
  };

  proto.showThankYou = function () {
    var ty = this.thankYou;
    var html = '<div class="sv-card sv-card--' + this.position + '">';
    html += '<button class="sv-close" aria-label="Close">' + ICONS.close + '</button>';
    html += '<div class="sv-thank-you">';
    html += '<div class="sv-thank-you-icon">' + ICONS.heart + '</div>';
    html += '<h4 class="sv-thank-you-title">' + (ty.title || 'Thank you!') + '</h4>';
    html += '<p class="sv-thank-you-subtitle">' + (ty.subtitle || 'Your feedback helps us improve.') + '</p>';
    html += '</div></div>';

    this.container.innerHTML = html;

    var self = this;
    this.container.querySelector('.sv-close').addEventListener('click', function () { self.hide(true); });

    if (ty.autoClose) {
      setTimeout(function () { self.hide(true); }, ty.autoClose);
    }
  };

})();
